import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { usePairingPaymentQuery } from "@/modules/pairingPayments/api/usePairingPayments.ts";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { Loader } from "@/components/Loader.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { PairingTransactionDetail } from "@/modules/pairingPayments/PairingTransactionDetail.tsx";
import { NOT_AVAILABLE } from "@/lib/utils.ts";
import { getLocation } from "@/lib/table.ts";
import { PAIRING_LOCATION_KEY } from "@/routes/payments/pairing/index.tsx";

export const Route = createFileRoute("/payments/pairing/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const title = t`Pairing payment detail`;

  const { data, isError, isPending, error } = usePairingPaymentQuery({ id: parseInt(id) });

  const sortedHistory = useMemo(() => {
    if (!data?.paymentPairingStatusData) return [];
    return [...data.paymentPairingStatusData].sort((a, b) => {
      if (a.createdAt === undefined || b.createdAt === undefined) return 0;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [data?.paymentPairingStatusData]);

  return (
    <PageWrapper>
      {isError && (
        <FetchingError
          message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
          error={error}
        />
      )}
      {isPending && <Loader message={t`Loading pairing payment...`} />}
      {data && (
        <>
          <PageHeader
            breadcrumbs={[
              { href: `/payments/pairing/${getLocation(PAIRING_LOCATION_KEY)}`, title: t`Pairing payments` },
              { href: "/", title },
            ]}
          />
          <PairingTransactionDetail
            amount={data.amount?.value ?? 0}
            currency={data.amount?.currency ?? NOT_AVAILABLE}
            debtorIban={data.debtor?.accountIdentification?.value ?? NOT_AVAILABLE}
            debtorName={data.debtor?.accountIdentification?.value ?? NOT_AVAILABLE}
            creditorIban={data.creditor?.accountIdentification?.value ?? NOT_AVAILABLE}
            creditorName={data.creditor?.accountIdentification?.value ?? NOT_AVAILABLE}
            finishPairingAt={data.finishPairingAt ?? NOT_AVAILABLE}
            pairingStatus={sortedHistory[sortedHistory.length - 1]?.paymentPairingStatus ?? NOT_AVAILABLE}
            updatedAt={sortedHistory[sortedHistory.length - 1]?.createdAt}
            createdAt={data.paymentPairingStatusData?.[0]?.createdAt}
            pairingId={data.uniquePaymentPairingId}
            reference={data.remittanceInformation.structured.reference ?? NOT_AVAILABLE}
            strategy={data?.pairingStrategy ?? NOT_AVAILABLE}
            history={sortedHistory}
            foundData={data.foundPaymentData ?? []}
            className={"mb-6"}
          />
        </>
      )}
    </PageWrapper>
  );
}
